import { api, persistRefreshToken } from './api';
import { useAuthStore } from '../stores/auth.store';

interface User {
  id: string;
  email: string;
  nombre: string;
  hogarId: string;
  rol: 'OWNER' | 'ADMIN' | 'MIEMBRO';
}

interface AuthResponse {
  user: User;
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

interface RefreshResponse {
  accessToken: string;
}

interface RegisterData {
  email: string;
  password: string;
  nombre: string;
  nombreHogar: string;
}

interface LoginData {
  email: string;
  password: string;
}

export const authService = {
  async register(data: RegisterData): Promise<{ success: boolean; error?: string }> {
    useAuthStore.getState().setLoading(true);

    const response = await api.post<AuthResponse>('/auth/register', data);

    if (response.success && response.data) {
      useAuthStore.getState().setAuth(
        response.data.user,
        response.data.tokens.accessToken,
        response.data.tokens.refreshToken
      );
      // C15: persist refresh token to secureStorage (Keychain/EncryptedSharedPreferences
      // on native, localStorage fallback on web). Fire-and-forget — store is the
      // source of truth in-memory; secureStorage just survives cold start.
      await persistRefreshToken(response.data.tokens.refreshToken);
      return { success: true };
    }

    useAuthStore.getState().setLoading(false);
    return {
      success: false,
      error: response.error?.message || 'Error al registrar',
    };
  },

  async login(data: LoginData): Promise<{ success: boolean; error?: string }> {
    // Rediseño F2 (hallazgo de QA en vivo): si cualquier paso lanza entre
    // setLoading(true) y el retorno, isLoading quedaba atorado en true y el
    // botón de login moría deshabilitado hasta recargar. El finally garantiza
    // que el spinner SIEMPRE se libera (setAuth también lo apaga; redundante
    // pero inofensivo).
    useAuthStore.getState().setLoading(true);
    try {
      const response = await api.post<AuthResponse>('/auth/login', data);

      if (response.success && response.data) {
        useAuthStore.getState().setAuth(
          response.data.user,
          response.data.tokens.accessToken,
          response.data.tokens.refreshToken
        );
        // C15: persist to secureStorage (see register comment).
        await persistRefreshToken(response.data.tokens.refreshToken);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || 'Error al iniciar sesión',
      };
    } finally {
      useAuthStore.getState().setLoading(false);
    }
  },

  async logout(): Promise<void> {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (refreshToken) {
      await api.post('/auth/logout', { refreshToken });
    }
    // C15: clear secureStorage so the token is not resurrected on next boot.
    await persistRefreshToken(null);
    useAuthStore.getState().logout();
  },

  async refreshToken(): Promise<boolean> {
    const currentRefreshToken = useAuthStore.getState().refreshToken;
    if (!currentRefreshToken) {
      await persistRefreshToken(null);
      useAuthStore.getState().logout();
      return false;
    }

    const response = await api.post<RefreshResponse>('/auth/refresh', {
      refreshToken: currentRefreshToken,
    });

    if (response.success && response.data) {
      useAuthStore.getState().setAccessToken(response.data.accessToken);
      return true;
    }

    await persistRefreshToken(null);
    useAuthStore.getState().logout();
    return false;
  },

  // AUTH-001: Cambiar contraseña
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    const response = await api.post<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
    });

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error?.message || 'Error al cambiar contraseña',
    };
  },

  // AUTH-002: Solicitar recuperación de contraseña
  async forgotPassword(email: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email });

    if (response.success && response.data) {
      return { success: true, message: response.data.message };
    }

    return {
      success: false,
      error: response.error?.message || 'Error al solicitar recuperación',
    };
  },

  // AUTH-002: Resetear contraseña con token
  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    const response = await api.post<{ message: string }>('/auth/reset-password', {
      token,
      newPassword,
    });

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error?.message || 'Error al restablecer contraseña',
    };
  },

  // ============================================================================
  // V2-007: GDPR - Eliminación de cuenta
  // ============================================================================

  // Solicitar eliminación de cuenta (30 días de gracia)
  async requestAccountDeletion(): Promise<{ success: boolean; deletionDate?: Date; error?: string }> {
    const response = await api.delete<{ message: string; deletionDate: string }>('/auth/me');

    if (response.success && response.data) {
      return {
        success: true,
        deletionDate: new Date(response.data.deletionDate),
      };
    }

    return {
      success: false,
      error: response.error?.message || 'Error al solicitar eliminación',
    };
  },

  // Cancelar solicitud de eliminación
  async cancelAccountDeletion(): Promise<{ success: boolean; error?: string }> {
    const response = await api.post<{ message: string }>('/auth/me/cancel-deletion');

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error?.message || 'Error al cancelar solicitud',
    };
  },

  // Obtener estado de eliminación
  async getDeletionStatus(): Promise<{
    success: boolean;
    data?: {
      hasPendingDeletion: boolean;
      deletionRequestedAt: Date | null;
      deletionDate: Date | null;
      daysRemaining: number | null;
    };
    error?: string;
  }> {
    const response = await api.get<{
      hasPendingDeletion: boolean;
      deletionRequestedAt: string | null;
      deletionDate: string | null;
      daysRemaining: number | null;
    }>('/auth/me/deletion-status');

    if (response.success && response.data) {
      return {
        success: true,
        data: {
          hasPendingDeletion: response.data.hasPendingDeletion,
          deletionRequestedAt: response.data.deletionRequestedAt ? new Date(response.data.deletionRequestedAt) : null,
          deletionDate: response.data.deletionDate ? new Date(response.data.deletionDate) : null,
          daysRemaining: response.data.daysRemaining,
        },
      };
    }

    return {
      success: false,
      error: response.error?.message || 'Error al obtener estado',
    };
  },

  // ============================================================================
  // V2-012: Clean Slate - Limpiar todos los datos financieros
  // ============================================================================

  // C29 PR-2 (M1): la confirmación por nombre del hogar es OBLIGATORIA y se
  // valida en el backend contra la base. Enviarla desde aquí no es la defensa —
  // es el vehículo; la defensa está del otro lado.
  async cleanSlate(hogarNombre: string): Promise<{
    success: boolean;
    deletedCounts?: Record<string, number>;
    error?: string;
  }> {
    const response = await api.delete<{
      message: string;
      deletedCounts: Record<string, number>;
    }>('/auth/me/clean-slate', { hogarNombre });

    if (response.success && response.data) {
      return {
        success: true,
        deletedCounts: response.data.deletedCounts,
      };
    }

    return {
      success: false,
      error: response.error?.message || 'Error al limpiar datos',
    };
  },
};
